import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Card, Col, Row, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import adSettingsApi from '../../../api/watconnect/AdSettingsApi';
import ObjectConfigApi from '../../../api/ObjectConfigApi';
import FieldMappingEditor, { UniqueFieldUpsertHint } from './FieldMappingEditor';
import { buildMappingPayload, parseStoredMapping } from './fieldMappingUtils';

/**
 * Detail custom tab for watconnect_ads_setting.
 * Uses watconnect_ads_setting.id, page_id, form_id, field_mapping (not watconnect_config).
 * recordId from DetailLayout when on ads setting detail; otherwise fetches the single row.
 */
const WatconnectAdSettingsTab = ({ recordId, recordData, onRefresh }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [adsSetting, setAdsSetting] = useState(null);
  const [activeRecordId, setActiveRecordId] = useState('');
  const [leadFields, setLeadFields] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [mappingRows, setMappingRows] = useState([]);
  const [webhookUrl, setWebhookUrl] = useState('');
  const [questionsError, setQuestionsError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  const loadRecord = useCallback(async () => {
    setLoading(true);
    setError(null);
    setQuestionsError(null);
    try {
      let record = null;
      if (recordId) {
        const recordRes = await adSettingsApi.getById(recordId);
        if (!recordRes.data?.success || !recordRes.data.data) {
          setError(recordRes.data?.message || 'Failed to load ads settings');
          return;
        }
        record = recordRes.data.data;
      } else {
        const currentRes = await adSettingsApi.fetchCurrent();
        if (!currentRes.data?.success || !currentRes.data.data) {
          setError(
            currentRes.data?.message || 'No watconnect_ads_setting record found'
          );
          return;
        }
        record = currentRes.data.data;
      }

      if (!record?.page_id || !record?.form_id) {
        setError('page_id and form_id must be set on watconnect_ads_setting.');
        return;
      }

      setAdsSetting(record);
      setActiveRecordId(record.id);

      const [questionsRes, leadConfigRes] = await Promise.all([
        adSettingsApi.fetchFormQuestions({
          adsSettingId: record.id,
          page_id: record.page_id,
          form_id: record.form_id,
        }),
        ObjectConfigApi.fetchConfigObjectByNameWithFields('lead'),
      ]);

      const fields = leadConfigRes?.data?.fields || [];
      const editableFields = fields.filter(
        (field) =>
          field.is_deleted !== true &&
          field.is_readonly !== true &&
          !['id', 'createddate', 'lastmodifieddate', 'createdbyid', 'lastmodifiedbyid'].includes(
            String(field.field_name || field.fieldName || '').toLowerCase()
          )
      );
      setLeadFields(editableFields);

      if (questionsRes.data?.success) {
        setQuestions(Array.isArray(questionsRes.data.questions) ? questionsRes.data.questions : []);
      } else {
        setQuestions([]);
        setQuestionsError(
          questionsRes.data?.message || 'Failed to load Facebook form questions'
        );
      }

      setMappingRows(parseStoredMapping(record.field_mapping, editableFields));
      setIsEditing(false);

      try {
        const webhookRes = await adSettingsApi.fetchWebhookUrl(record.id);
        if (webhookRes.data?.success) {
          setWebhookUrl(webhookRes.data.data?.webhook_url || '');
        }
      } catch {
        setWebhookUrl('');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load ads settings');
    } finally {
      setLoading(false);
    }
  }, [recordId]);

  useEffect(() => {
    loadRecord();
  }, [loadRecord, recordData]);

  const handleCopyWebhook = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Webhook URL copied.');
    } catch {
      toast.error('Could not copy webhook URL.');
    }
  };

  const handleCancelEdit = () => {
    setMappingRows(parseStoredMapping(adsSetting?.field_mapping, leadFields));
    setIsEditing(false);
  };

  const handleSave = async (e) => {
    e?.preventDefault();
    if (!activeRecordId) return;

    let fieldMapping;
    try {
      fieldMapping = buildMappingPayload(mappingRows);
    } catch (err) {
      toast.error(err.message);
      return;
    }

    setSaving(true);
    try {
      const res = await adSettingsApi.updateRecord(activeRecordId, {
        field_mapping: fieldMapping,
      });
      if (res.data?.success) {
        toast.success('Mapping saved.');
        setIsEditing(false);
        if (typeof onRefresh === 'function') {
          onRefresh();
        }
        await loadRecord();
      } else {
        toast.error(res.data?.message || 'Save failed.');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" size="sm" className="me-2" />
        Loading ads settings…
      </div>
    );
  }

  if (error) {
    return <Alert variant="danger">{error}</Alert>;
  }

  return (
    <Card className="border-0 shadow-sm">
      <Card.Body className="p-4">
        {adsSetting && (
          <Row className="mb-3 g-3">
            <Col md={6}>
              <div className="small text-muted">Page ID</div>
              <div className="fw-semibold">{adsSetting.page_id || '—'}</div>
            </Col>
            <Col md={6}>
              <div className="small text-muted">Form ID</div>
              <div className="fw-semibold">{adsSetting.form_id || '—'}</div>
            </Col>
          </Row>
        )}

        <FieldMappingEditor
          rows={mappingRows}
          onRowsChange={setMappingRows}
          questions={questions}
          leadFields={leadFields}
          loadingQuestions={false}
          questionsError={questionsError}
          webhookUrl={webhookUrl}
          onCopyWebhook={handleCopyWebhook}
          readOnly={!isEditing}
        />

        <div className="mt-4 d-flex flex-wrap justify-content-between align-items-center gap-2">
          {isEditing ? <UniqueFieldUpsertHint /> : null}
          <div className="d-flex gap-2 ms-auto">
            {!isEditing ? (
              <Button variant="primary" onClick={() => setIsEditing(true)}>
                <i className="fas fa-edit me-1 text-white" aria-hidden />
                Edit Mapping
              </Button>
            ) : (
              <>
                <Button variant="light" onClick={handleCancelEdit} disabled={saving}>
                  Cancel
                </Button>
                <Button
                  variant="success"
                  disabled={saving}
                  onClick={handleSave}
                >
                  {saving ? 'Saving…' : 'Save Mapping'}
                </Button>
              </>
            )}
          </div>
        </div>
      </Card.Body>
    </Card>
  );
};

export default WatconnectAdSettingsTab;
